import { supabase } from '../../backend/supabase';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;

export interface MigrationStats {
    total: number;
    migrated: number;
    skipped: number;
    failed: number;
    errors: string[];
}

// This function runs ONE migration batch
export const runMigrationBatch = async (accessToken: string, dryRun: boolean, targetGameId?: string): Promise<MigrationStats> => {
    if (!supabaseUrl) {
        throw new Error('Supabase URL not configured.');
    }
    
    const functionUrl = `${supabaseUrl}/functions/v1/migrate-code-to-storage`;
    
    const payload: { dryRun: boolean; gameId?: string } = { dryRun: dryRun };
    if (targetGameId) {
        payload.gameId = targetGameId;
    }

    try {
        console.log(`Attempting to call Supabase Edge Function...`);
        const response = await fetch(functionUrl, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`, 
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload), // Send dryRun and optional gameId
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to parse error response' }));
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.message || response.statusText}`);
        }

        const data = await response.json();
        console.log('Migration batch response:', data);
        
        const stats: MigrationStats = data.stats;
        
        return stats;

    } catch (error) {
        console.error('Error calling migrate-code-to-storage function:', error);
        // Return a failed stats object on network/fetch error to avoid crashing the loop
        return {
            total: 0,
            migrated: 0,
            skipped: 0,
            failed: 0,
            errors: [String(error)],
        };
    }
};

// This function handles the iterative calling and is the main export for the button click
export const runCodeMigration = async (
    gameId: string, 
    dryRun: boolean, 
    runAll: boolean, 
    cancelRef: React.MutableRefObject<boolean>,
    setBatchCounter: React.Dispatch<React.SetStateAction<number>>,
    setTotalRemaining: React.Dispatch<React.SetStateAction<number | null>>,
    setIsMigrating: React.Dispatch<React.SetStateAction<boolean>>,
) => {
    if (!process.env.REACT_APP_SUPABASE_URL) {
        console.error('Supabase URL not configured.');
        console.error('Migration failed: Supabase URL not configured.');
        return;
    }

    const { data: { session }, error: sessionError } = await supabase.auth.getSession();

    if (sessionError || !session) {
        console.error('Failed to get user session:', sessionError);
        console.error('Migration failed: Please log in again.');
        return;
    }
    
    setIsMigrating(true);
    cancelRef.current = false; // Reset cancellation flag at the start
    setBatchCounter(0); // Reset batch counter
    setTotalRemaining(null); // Reset remaining count at start
    const accessToken = session.access_token;
    let totalMigrated = 0;
    let currentRemaining = 0;
    
    // Determine the target Game ID, trimming whitespace
    const targetGameId = gameId.trim() || undefined;
    // If a game ID is present, we only run one batch, regardless of the runAll checkbox
    const shouldRunAll = !targetGameId && runAll; 

    const cumulativeStats: MigrationStats = {
        total: 0,
        migrated: 0,
        skipped: 0,
        failed: 0,
        errors: [],
    };

    try {
        console.log(`--- Starting Migration (Dry Run: ${dryRun}, Run All: ${shouldRunAll}, Target Game ID: ${targetGameId || 'None'}) ---`);
        
        // Loop while `shouldRunAll` is true. If `shouldRunAll` is false, it runs exactly once.
        while (true) {

            if (cancelRef.current) {
                console.log('--- Migration stopped by user cancellation. ---');
                break;
            }
            
            const batchStats = await runMigrationBatch(accessToken, dryRun, targetGameId);
            
            // On the first run, set the overall total count
            if (cumulativeStats.total === 0) {
                cumulativeStats.total = batchStats.total;
            }

            // Update cumulative stats
            cumulativeStats.migrated += batchStats.migrated;
            cumulativeStats.skipped += batchStats.skipped;
            cumulativeStats.failed += batchStats.failed;
            cumulativeStats.errors.push(...batchStats.errors);
            
            totalMigrated = cumulativeStats.migrated;
            
            const entriesProcessedInBatch = batchStats.migrated + batchStats.skipped + batchStats.failed;

            // Update the total remaining by subtracting the cumulative successful/skipped/failed from the initial total
            // Only update remaining if the total was successfully fetched (i.e., not 0 on the first run)
            if (cumulativeStats.total > 0) {
                currentRemaining = cumulativeStats.total - (cumulativeStats.migrated + cumulativeStats.skipped + cumulativeStats.failed);
                setTotalRemaining(Math.max(0, currentRemaining));
            } else {
                currentRemaining = 0;
                setTotalRemaining(0);
            }

            // Increment batch counter AFTER a successful call
            setBatchCounter(prev => prev + 1);
            
            // If the sum of processed entries in this batch is 0, the query returned no more entries, and the migration is done.
            if (entriesProcessedInBatch === 0) {
                console.log(`Migration complete.`);
                break;
            }
            
            console.log(`Batch complete. Processed ${entriesProcessedInBatch} entries (Migrated: ${batchStats.migrated}, Skipped: ${batchStats.skipped}, Failed: ${batchStats.failed}). Total migrated so far: ${totalMigrated}. Remaining: ${currentRemaining}`);
            
            // Add a delay between calls to be a good citizen and avoid potential rate limits
            await new Promise(resolve => setTimeout(resolve, 500));

            // Break condition for non-looping mode (either 'Run All' is false, or a target game ID was specified)
            if (!shouldRunAll) {
                console.log(`--- Migration stopped after one batch (Run All is false or Target Game ID was set). ---`);
                break;
            }
        }
        
        // Console log summary of all stats across rounds
        console.log(`--- FINAL MIGRATION SUMMARY (Dry Run: ${dryRun}) ---`);
        console.log(`Initial Total Entries Needing Migration: ${cumulativeStats.total}`);
        console.log(`Total Successfully Migrated: ${cumulativeStats.migrated}`);
        console.log(`Total Skipped: ${cumulativeStats.skipped}`);
        console.log(`Total Failed: ${cumulativeStats.failed}`);
        const finalProcessed = cumulativeStats.migrated + cumulativeStats.skipped + cumulativeStats.failed;
        const finalRemaining = Math.max(0, cumulativeStats.total - finalProcessed);
        console.log(`Total Entries Processed: ${finalProcessed}`);
        console.log(`Final Remaining (based on initial count): ${finalRemaining}`);

        if (cumulativeStats.errors.length > 0) {
            console.log('Errors encountered:');
            cumulativeStats.errors.forEach(error => console.log(`- ${error}`));
        }
        console.log('----------------------------------------------------');


    } catch (error) {
        console.error(`--- Migration failed! See console for details. Error: ${error instanceof Error ? error.message : String(error)} ---`);
    } finally {
        setIsMigrating(false);
        cancelRef.current = false;
        setBatchCounter(0); // Clear batch counter
    }
};

export const onCancelMigrationClick = (cancelRef: React.MutableRefObject<boolean>) => {
    console.log('--- Migration cancellation requested. Waiting for current batch to finish. ---');
    cancelRef.current = true;
}